import React from 'react';

/**
 * SvgLineChart — renders a multi-series line/area chart as proper SVG JSX.
 * Replaces the old svgLine() string-builder that required dangerouslySetInnerHTML.
 *
 * Props:
 *   data    — array of objects, each with a `lbl` string and numeric series keys
 *   series  — array of { k: string, c: color, w?: strokeWidth, fill?: bool, dash?: bool }
 *   height  — SVG height in px (default 190)
 */
export function SvgLineChart({ data, series, height = 190 }) {
  if (!data || !data.length) return null;

  const W = 840,
    ML = 54,
    MR = 12,
    MT = 10,
    MB = 28;
  const CW = W - ML - MR;
  const CH = height - MT - MB;

  const vals = data.flatMap((d) => series.map((s) => d[s.k] || 0)).filter((v) => !isNaN(v));
  const maxV = Math.max(...vals, 1);

  const xS = (i) => ML + (i / (data.length - 1 || 1)) * CW;
  const yS = (v) => MT + CH - (v / maxV) * CH;

  const step = Math.max(1, Math.ceil(data.length / 7));

  return (
    <svg
      viewBox={`0 0 ${W} ${height}`}
      width="100%"
      style={{ display: 'block', overflow: 'visible' }}
    >
      <defs>
        {series
          .filter((s) => s.fill)
          .map((s) => (
            <linearGradient key={s.k} id={`g${s.k}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.c} stopOpacity={0.18} />
              <stop offset="100%" stopColor={s.c} stopOpacity={0.01} />
            </linearGradient>
          ))}
      </defs>

      {/* Grid lines + y-axis labels */}
      {[0, 0.25, 0.5, 0.75, 1].map((t) => {
        const y = MT + CH * (1 - t);
        const v = maxV * t;
        const lbl = v >= 1000 ? `$${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `$${Math.round(v)}`;
        return (
          <g key={t}>
            <line
              x1={ML}
              y1={y.toFixed(1)}
              x2={ML + CW}
              y2={y.toFixed(1)}
              stroke="#2e2e40"
              strokeWidth={1}
            />
            <text
              x={ML - 5}
              y={(y + 4).toFixed(1)}
              textAnchor="end"
              fontSize={10}
              fill="#7a7a9a"
              fontFamily="DM Sans,sans-serif"
            >
              {lbl}
            </text>
          </g>
        );
      })}

      {/* Fill areas */}
      {series
        .filter((s) => s.fill)
        .map((s) => {
          const pts = data.map((d, i) => `${xS(i).toFixed(1)},${yS(d[s.k] || 0).toFixed(1)}`);
          const last = xS(data.length - 1).toFixed(1);
          const base = (MT + CH).toFixed(1);
          const d = `M ${pts[0]} L ${pts.join(' L ')} L ${last},${base} L ${xS(0).toFixed(1)},${base} Z`;
          return <path key={s.k} d={d} fill={`url(#g${s.k})`} />;
        })}

      {/* Lines */}
      {series.map((s) => (
        <polyline
          key={s.k}
          points={data.map((d, i) => `${xS(i).toFixed(1)},${yS(d[s.k] || 0).toFixed(1)}`).join(' ')}
          fill="none"
          stroke={s.c}
          strokeWidth={s.w || 2.5}
          strokeDasharray={s.dash ? '6,3' : undefined}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}

      {/* X-axis labels */}
      {data.map((d, i) => {
        if (i % step !== 0 && i !== data.length - 1) return null;
        return (
          <text
            key={i}
            x={xS(i).toFixed(1)}
            y={(MT + CH + 18).toFixed(1)}
            textAnchor="middle"
            fontSize={10}
            fill="#7a7a9a"
            fontFamily="DM Sans,sans-serif"
          >
            {d.lbl}
          </text>
        );
      })}
    </svg>
  );
}
